/**
 * @ken/module-classes — frontend entry point.
 *
 * Story-page migration (ADR-0081/0082): the in-list detail Sheet is RETIRED.
 * The classes module now mounts a list page at /classes (matching the
 * sidebar nav) + a routed full-page story detail at classes/$id (CONFIG
 * archetype — header + fact cards, NO hero). The class-name cell on the
 * list is a stretched <Link> to classes/$id/overview.
 */

import {
  lazyScreen,
  type AnyRoute,
  type CreateModuleRoutes,
} from "@ken/cockpit-types";
import { isRecordId, RecordNotFound } from "@ken/ui";
import { createRoute, notFound, redirect } from "@tanstack/react-router";

// One loader per screen file, shared by every screen that file exports, so the
// route table carries each file's import (and its preload list) once (SF-BUNDLE).
const loadClassStoryTabs = () => import("./routes/story/class-story-tabs");

// Screens download the first time they are opened (ADR-040, BND-01). The
// route table below stays eager: paths, params, validateSearch, redirects.
const ClassListPage = lazyScreen(
  () => import("./routes/class-list"),
  "ClassListPage",
);
const ClassStoryDetail = lazyScreen(
  () => import("./routes/story/class-story-detail"),
  "ClassStoryDetail",
);
const ClassActivityRoute = lazyScreen(
  loadClassStoryTabs,
  "ClassActivityRoute",
);
const ClassOverviewRoute = lazyScreen(
  loadClassStoryTabs,
  "ClassOverviewRoute",
);

// UI-06: classes have no create surface in the cockpit (they arrive through
// the QuickBooks connector), so this takes the item's "create surface out of
// scope" outcome — `/classes/new` and any other non-id segment render the
// shared RecordNotFound page rather than a blank screen.

export const createRoutes: CreateModuleRoutes = (parentRoute: AnyRoute) => {
  const listRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "classes",
    // The list's filter/search/highlight are URL-borne so back-navigation
    // from the story page restores them (ADR-0081 §3).
    validateSearch: (raw: Record<string, unknown>): ClassListSearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["highlight"] === "string" && raw["highlight"]
        ? { highlight: raw["highlight"] }
        : {}),
      // POP-01 (ADR-0229): the record open in the pop-up, so a reload
      // reopens it and the browser's back button closes it.
      ...(typeof raw["open"] === "string" && raw["open"]
        ? { open: raw["open"] }
        : {}),
      // POP-02 (ADR-0230): the pop-up's open tab.
      ...(typeof raw["tab"] === "string" && raw["tab"]
        ? { tab: raw["tab"] }
        : {}),
    }),
    component: ClassListPage,
  });

  // UI-06: a STATIC route for the literal `new`, so the id route below can
  // never match it and fire a detail read for a record called "new".
  const literalNewRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "classes/new",
    component: RecordNotFound,
  });

  // ── Story-page detail (ADR-0081): parent classes/$id shell + the routed
  //    Overview tab child + an index redirect to …/overview. ───────────────
  const detailRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "classes/$id",
    // UI-06's id-shape guard: a segment that could never be a record id stops
    // here, BEFORE any detail or graph request is issued.
    beforeLoad: ({ params }) => {
      if (!isRecordId((params as { id?: string }).id)) throw notFound();
    },
    notFoundComponent: RecordNotFound,
    // The list's filter/search ride into the detail route so Prev/Next walks
    // the SAME filtered set and back restores state.
    validateSearch: (raw: Record<string, unknown>): ClassStorySearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
    }),
    component: ClassStoryDetail,
  });

  // Index redirect classes/$id → …/overview (the default tab).
  const detailIndexRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "/",
    beforeLoad: ({ params }) => {
      throw redirect({
        to: "/$tenantSlug/classes/$id/overview",
        params: params as never,
        replace: true,
      });
    },
  });

  const overviewRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "overview",
    component: ClassOverviewRoute,
  });

  const activityRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "activity",
    component: ClassActivityRoute,
  });

  (detailRoute as unknown as { addChildren: (c: unknown) => void }).addChildren(
    [detailIndexRoute, overviewRoute, activityRoute],
  );

  // Static before parameterised.
  return [listRoute, literalNewRoute, detailRoute];
};

/** List search params (filter/search/highlight) — URL-borne for restore. */
export interface ClassListSearch {
  q?: string;
  filter_status?: string;
  highlight?: string;
  /** POP-01: the record open in the pop-up over the list. */
  open?: string;
  /** POP-02: the pop-up's open tab (absent: Overview). */
  tab?: string;
}

/** Story-detail search params (carry the list's filter/search). */
export interface ClassStorySearch {
  q?: string;
  filter_status?: string;
}
